import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';
import { 
  Shield, Lock, Eye, MessageSquare, UserX, Clock,
  ChevronRight, Users, CheckCircle, Zap, ChevronDown,
  ArrowRight, Menu, X, EyeOff, Send, Fingerprint
} from 'lucide-react';
import { trackCTAClick, initScrollTracking, initTimeTracking } from '@/utils/analytics';

// AEO-Optimized Schema - FAQ + Organization + Software
const seoSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://privluxe.com/#organization",
      "name": "Privluxe",
      "url": "https://privluxe.com",
      "description": "Privluxe is a private messaging platform designed for discreet, anonymous communication without revealing your identity.",
      "logo": {
        "@type": "ImageObject",
        "url": "https://privluxe.com/logo.png"
      }
    },
    {
      "@type": "WebSite",
      "@id": "https://privluxe.com/#website",
      "url": "https://privluxe.com",
      "name": "Privluxe - Send Private Messages Without Revealing Your Identity",
      "description": "Privluxe lets you start secure, discreet conversations without sharing your phone number, email, or personal details.",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://privluxe.com/search?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    },
    {
      "@type": "SoftwareApplication",
      "name": "Privluxe",
      "applicationCategory": "CommunicationApplication",
      "operatingSystem": "Web",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "description": "Private messaging app for anonymous, discreet communication. Send messages without sharing your phone number or revealing your identity.",
      "featureList": [
        "Anonymous messaging",
        "No phone number required",
        "Disappearing messages",
        "Invite-based chat",
        "Identity protection",
        "Secure conversations"
      ],
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "ratingCount": "150"
      }
    },
    {
      "@type": "HowTo",
      "name": "How to Send Anonymous Messages Without Revealing Your Phone Number",
      "description": "Learn how to start private conversations without sharing your personal contact information using Privluxe.",
      "step": [
        {
          "@type": "HowToStep",
          "position": 1,
          "name": "Create Your Account",
          "text": "Sign up with your email or phone. This info stays private and is never shared with anyone you chat with."
        },
        {
          "@type": "HowToStep",
          "position": 2,
          "name": "Send an Anonymous Invite",
          "text": "Share an invite link via SMS, email, or any messenger. The recipient won't see your personal contact details."
        },
        {
          "@type": "HowToStep",
          "position": 3,
          "name": "Chat Privately",
          "text": "Once they accept, start your secure conversation. Send text, voice notes, and view-once media."
        }
      ]
    },
    {
      "@type": "WebPage",
      "speakable": {
        "@type": "SpeakableSpecification",
        "cssSelector": ["#what-is-privluxe", "#how-it-works", "#faq"]
      },
      "mainEntity": {
        "@type": "Question",
        "name": "Can you send anonymous messages without revealing your phone number?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Privluxe allows you to communicate without sharing your identity. You can start conversations, send messages, and exchange voice notes without ever revealing your phone number, email, or personal details to the other person."
        }
      }
    },
    {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Can I send messages without sharing my phone number?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Privluxe allows you to start conversations without revealing your phone number or email. Your contact details are never shared with the other person."
          }
        },
        {
          "@type": "Question",
          "name": "Is Privluxe anonymous?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Privluxe protects your identity. Your personal details are never shared with the other user. You choose what to reveal and when."
          }
        },
        {
          "@type": "Question",
          "name": "Are messages secure and private?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Messages are designed for privacy, including disappearing message options and controlled access. Only you and your recipient can see your conversations."
          }
        },
        {
          "@type": "Question",
          "name": "Can someone know I use Privluxe?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No. We do not reveal whether a person is using Privluxe. Your usage is completely private."
          }
        },
        {
          "@type": "Question",
          "name": "How do I invite someone to chat?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "You can send an anonymous invite link via SMS, email, or copy a link to share. The recipient won't see your personal contact details."
          }
        },
        {
          "@type": "Question",
          "name": "What happens to messages after I read them?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "You can set messages to disappear after being viewed, or keep them. You control whether messages stay or vanish."
          }
        },
        {
          "@type": "Question",
          "name": "Is Privluxe free?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Privluxe is free to use with all core privacy features included."
          }
        },
        {
          "@type": "Question",
          "name": "How is Privluxe different from WhatsApp?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "On WhatsApp, anyone you message can see your phone number. On Privluxe, they cannot. Your contact information stays completely hidden unless you choose to share it."
          }
        }
      ]
    }
  ]
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const cleanupScroll = initScrollTracking();
    const cleanupTime = initTimeTracking();
    return () => {
      cleanupScroll();
      cleanupTime();
    };
  }, []);

  const handleCTAClick = (location) => {
    trackCTAClick(location, 'Enter Privluxe');
    navigate('/login');
  };

  // FAQ data for AEO
  const faqs = [
    {
      q: "Can I send messages without sharing my phone number?",
      a: "Yes. Privluxe allows you to start conversations without revealing your phone number or email. Your contact details are never shared with the other person."
    },
    {
      q: "Is Privluxe anonymous?",
      a: "Yes. Privluxe protects your identity. Your personal details are never shared with the other user. You choose what to reveal and when."
    },
    {
      q: "Are messages secure and private?",
      a: "Yes. Messages are designed for privacy, including disappearing message options and controlled access. Only you and your recipient can see your conversations."
    },
    {
      q: "Can someone know I use Privluxe?",
      a: "No. We do not reveal whether a person is using Privluxe. Your usage is completely private."
    },
    {
      q: "How do I invite someone to chat?",
      a: "You can send an anonymous invite link via SMS, email, or copy a link to share anywhere. The recipient won't see your personal contact details until you choose to reveal them."
    },
    {
      q: "What happens to messages after I read them?",
      a: "You control your messages. You can set messages to disappear after being viewed (view-once), or keep them. You decide whether messages stay or vanish."
    },
    {
      q: "Is Privluxe free to use?",
      a: "Yes. Privluxe is completely free to use with all core privacy features included. No hidden costs."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Privluxe - Send Private Messages Without Revealing Your Identity</title>
        <meta name="description" content="Privluxe lets you start secure, discreet conversations without sharing your phone number, email, or personal details. Anonymous messaging for private communication." />
        <meta name="keywords" content="private messaging, anonymous chat, discreet communication, secret messages, hidden conversations, secure chat, no phone number messaging, anonymous communication" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://privluxe.com/" />
        <meta property="og:title" content="Privluxe - Send Private Messages Without Revealing Your Identity" />
        <meta property="og:description" content="Start secure, discreet conversations without sharing your phone number or email. Anonymous messaging made simple." />
        <meta property="og:image" content="https://privluxe.com/og-image.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Privluxe - Private Messages Without Revealing Identity" />
        <meta name="twitter:description" content="Send anonymous messages without sharing your phone number. Discreet, secure, private." />
        
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://privluxe.com/" />
        
        <script type="application/ld+json">
          {JSON.stringify(seoSchema)}
        </script>
      </Helmet>

    <div className="min-h-screen" style={{ background: '#020617' }}>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50" style={{ background: 'rgba(2, 6, 23, 0.9)', backdropFilter: 'blur(20px)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Shield className="h-6 w-6" style={{ color: '#7C3AED' }} />
              <span className="text-lg font-bold tracking-[0.2em]" style={{ color: '#E5E7EB' }}>PRIVLUXE</span>
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#what-is-privluxe" className="text-sm hover:text-white transition-colors" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>What is Privluxe</a>
              <a href="#how-it-works" className="text-sm hover:text-white transition-colors" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>How It Works</a>
              <a href="#when-to-use" className="text-sm hover:text-white transition-colors" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>When to Use</a>
              <a href="#faq" className="text-sm hover:text-white transition-colors" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>FAQ</a>
              <button 
                onClick={() => handleCTAClick('navbar_login')}
                className="text-sm hover:text-white transition-colors"
                style={{ color: 'rgba(229, 231, 235, 0.7)' }}
              >
                Login
              </button>
              <Button 
                onClick={() => handleCTAClick('navbar_cta')}
                className="text-white text-sm px-6"
                style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
              >
                Start Chatting
              </Button>
            </div>

            <button 
              className="md:hidden text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden px-4 py-4 space-y-4" style={{ background: 'rgba(10, 15, 44, 0.98)' }}>
            <a href="#what-is-privluxe" className="block text-sm py-2" style={{ color: '#E5E7EB' }}>What is Privluxe</a>
            <a href="#how-it-works" className="block text-sm py-2" style={{ color: '#E5E7EB' }}>How It Works</a>
            <a href="#when-to-use" className="block text-sm py-2" style={{ color: '#E5E7EB' }}>When to Use</a>
            <a href="#faq" className="block text-sm py-2" style={{ color: '#E5E7EB' }}>FAQ</a>
            <Button 
              onClick={() => handleCTAClick('mobile_menu_cta')}
              className="w-full text-white"
              style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
            >
              Start Chatting
            </Button>
          </div>
        )}
      </nav>

      {/* HERO SECTION - AEO Optimized */}
      <section className="relative min-h-screen flex items-center justify-center px-4 pt-16 overflow-hidden">
        <div 
          className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full opacity-30 blur-3xl pointer-events-none"
          style={{ background: 'radial-gradient(circle, #7C3AED 0%, transparent 70%)' }}
        />
        <div 
          className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full opacity-20 blur-3xl pointer-events-none"
          style={{ background: 'radial-gradient(circle, #22D3EE 0%, transparent 70%)' }}
        />

        <div className="relative z-10 text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8" style={{ background: 'rgba(124, 58, 237, 0.2)', border: '1px solid rgba(124, 58, 237, 0.3)' }}>
            <EyeOff className="h-4 w-4" style={{ color: '#7C3AED' }} />
            <span className="text-sm" style={{ color: '#E5E7EB' }}>Your Identity Stays Hidden</span>
          </div>

          {/* H1 - Primary AEO Target */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight" style={{ color: '#E5E7EB' }}>
            Send Private Messages<br />
            <span style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Without Revealing Your Identity
            </span>
          </h1>

          {/* Direct Answer for AI - Subheading */}
          <p className="text-lg sm:text-xl mb-10 max-w-2xl mx-auto" style={{ color: 'rgba(229, 231, 235, 0.8)' }}>
            Privluxe lets you start secure, discreet conversations without sharing your phone number, email, or personal details.
          </p>

          <div className="flex flex-col items-center gap-2">
            <Button 
              onClick={() => handleCTAClick('hero_primary')}
              className="h-14 px-8 text-white text-lg font-semibold transition-all hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
              data-testid="hero-cta-button"
            >
              Start Private Chat
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <span className="text-xs" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>No cost. No identity exposure.</span>
          </div>

          {/* Trust signals */}
          <div className="flex flex-wrap justify-center gap-6 mt-10">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" style={{ color: '#22D3EE' }} />
              <span className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>No phone number required</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" style={{ color: '#22D3EE' }} />
              <span className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>No signup fees</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" style={{ color: '#22D3EE' }} />
              <span className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>Messages disappear</span>
            </div>
          </div>
        </div>
      </section>

      {/* DIRECT ANSWER BLOCK - Critical for AEO */}
      <section className="py-12 px-4" style={{ background: 'linear-gradient(180deg, #020617 0%, rgba(10, 15, 44, 0.8) 100%)' }}>
        <div className="max-w-3xl mx-auto">
          <div className="p-6 sm:p-8 rounded-2xl" style={{ background: 'rgba(124, 58, 237, 0.08)', border: '1px solid rgba(124, 58, 237, 0.2)' }}>
            <div className="flex items-start gap-4">
              <MessageSquare className="h-8 w-8 shrink-0 mt-1" style={{ color: '#7C3AED' }} />
              <div>
                <h2 className="text-xl sm:text-2xl font-bold mb-3" style={{ color: '#E5E7EB' }}>
                  Can you send anonymous messages without revealing your phone number?
                </h2>
                <p className="text-base leading-relaxed" style={{ color: 'rgba(229, 231, 235, 0.85)' }}>
                  <strong style={{ color: '#22D3EE' }}>Yes.</strong> Privluxe allows you to communicate without sharing your identity. 
                  You can start conversations, send messages, and exchange voice notes without ever revealing your phone number, 
                  email, or personal details to the other person. Your contact information stays completely private.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT IS PRIVLUXE - Critical for AEO */}
      <section id="what-is-privluxe" className="py-20 px-4" style={{ background: 'rgba(10, 15, 44, 0.5)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ color: '#E5E7EB' }}>
            What is Privluxe?
          </h2>
          <p className="text-lg mb-8 max-w-3xl mx-auto" style={{ color: 'rgba(229, 231, 235, 0.8)' }}>
            Privluxe is a <strong>private messaging platform</strong> designed for <strong>discreet communication</strong>. 
            You can send messages, voice notes, and invites without revealing your identity or personal contact details.
          </p>
          <p className="text-base mb-8" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>
            If you want to communicate secretly or anonymously, Privluxe allows you to do that easily and safely. 
            Your phone number and email stay hidden. You choose what to share and when.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            <div className="p-6 rounded-xl" style={{ background: 'rgba(124, 58, 237, 0.1)', border: '1px solid rgba(124, 58, 237, 0.2)' }}>
              <UserX className="h-10 w-10 mx-auto mb-4" style={{ color: '#7C3AED' }} />
              <h3 className="text-lg font-semibold mb-2" style={{ color: '#E5E7EB' }}>Anonymous Invites</h3>
              <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>Send invite links without exposing your contact info</p>
            </div>
            <div className="p-6 rounded-xl" style={{ background: 'rgba(34, 211, 238, 0.1)', border: '1px solid rgba(34, 211, 238, 0.2)' }}>
              <Clock className="h-10 w-10 mx-auto mb-4" style={{ color: '#22D3EE' }} />
              <h3 className="text-lg font-semibold mb-2" style={{ color: '#E5E7EB' }}>Disappearing Messages</h3>
              <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>Set messages to vanish after they're viewed</p>
            </div>
            <div className="p-6 rounded-xl" style={{ background: 'rgba(124, 58, 237, 0.1)', border: '1px solid rgba(124, 58, 237, 0.2)' }}>
              <Shield className="h-10 w-10 mx-auto mb-4" style={{ color: '#7C3AED' }} />
              <h3 className="text-lg font-semibold mb-2" style={{ color: '#E5E7EB' }}>Identity Protection</h3>
              <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>Your real identity is never revealed to others</p>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4" style={{ color: '#E5E7EB' }}>
            How Privluxe Works
          </h2>
          <p className="text-center mb-12 max-w-2xl mx-auto" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>
            Start having private conversations in 3 simple steps
          </p>
          
          <div className="space-y-8">
            <div className="flex items-start gap-6">
              <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 text-xl font-bold" style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)', color: 'white' }}>1</div>
              <div>
                <h3 className="text-xl font-semibold mb-2" style={{ color: '#E5E7EB' }}>Create Your Account</h3>
                <p style={{ color: 'rgba(229, 231, 235, 0.6)' }}>Sign up with your email or phone. This info stays private and is never shared with anyone you chat with.</p>
              </div>
            </div>
            <div className="flex items-start gap-6">
              <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 text-xl font-bold" style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)', color: 'white' }}>2</div>
              <div>
                <h3 className="text-xl font-semibold mb-2" style={{ color: '#E5E7EB' }}>Send an Anonymous Invite</h3>
                <p style={{ color: 'rgba(229, 231, 235, 0.6)' }}>Share an invite link via SMS, email, or any messenger. The recipient won't see your personal contact details.</p>
              </div>
            </div>
            <div className="flex items-start gap-6">
              <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 text-xl font-bold" style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)', color: 'white' }}>3</div>
              <div>
                <h3 className="text-xl font-semibold mb-2" style={{ color: '#E5E7EB' }}>Chat Privately</h3>
                <p style={{ color: 'rgba(229, 231, 235, 0.6)' }}>Once they accept, start your secure conversation. Send text, voice notes, and view-once media.</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-center gap-2 mt-12">
            <Button 
              onClick={() => handleCTAClick('how_it_works_cta')}
              className="h-12 px-8 text-white font-semibold"
              style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
            >
              Start Private Chat
            </Button>
            <span className="text-xs" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>No cost. No identity exposure.</span>
          </div>
        </div>
      </section>

      {/* WHEN TO USE PRIVLUXE - Critical AEO Section */}
      <section id="when-to-use" className="py-20 px-4" style={{ background: 'rgba(10, 15, 44, 0.5)' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4" style={{ color: '#E5E7EB' }}>
            When Should You Use Privluxe?
          </h2>
          <p className="text-center mb-12 max-w-2xl mx-auto" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>
            Privluxe is the best choice when privacy matters more than identity
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <CheckCircle className="h-6 w-6 mb-3" style={{ color: '#22D3EE' }} />
              <h3 className="font-semibold mb-2" style={{ color: '#E5E7EB' }}>When you want to talk without sharing your number</h3>
              <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>Keep your phone number private while still having real conversations</p>
            </div>
            <div className="p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <CheckCircle className="h-6 w-6 mb-3" style={{ color: '#22D3EE' }} />
              <h3 className="font-semibold mb-2" style={{ color: '#E5E7EB' }}>When you need discreet or private conversations</h3>
              <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>For sensitive topics that require confidentiality</p>
            </div>
            <div className="p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <CheckCircle className="h-6 w-6 mb-3" style={{ color: '#22D3EE' }} />
              <h3 className="font-semibold mb-2" style={{ color: '#E5E7EB' }}>When you want messages to disappear</h3>
              <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>View-once messages that vanish after being read</p>
            </div>
            <div className="p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <CheckCircle className="h-6 w-6 mb-3" style={{ color: '#22D3EE' }} />
              <h3 className="font-semibold mb-2" style={{ color: '#E5E7EB' }}>When privacy matters more than identity</h3>
              <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>Communicate without revealing who you are</p>
            </div>
          </div>
        </div>
      </section>

      {/* WHY PRIVLUXE IS DIFFERENT - Comparison Context */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4" style={{ color: '#E5E7EB' }}>
            How is Privluxe Different From WhatsApp or Telegram?
          </h2>
          <p className="text-center mb-4 max-w-2xl mx-auto" style={{ color: 'rgba(229, 231, 235, 0.8)' }}>
            Unlike traditional messaging apps, Privluxe does not require you to share your phone number to start a conversation.
          </p>
          <p className="text-center mb-12 max-w-2xl mx-auto text-sm" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>
            On WhatsApp or Telegram, anyone you message can see your number. On Privluxe, they cannot.
          </p>
          
          <div className="overflow-x-auto">
            <table className="w-full text-left" style={{ color: '#E5E7EB' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <th className="py-4 px-4 font-semibold">Feature</th>
                  <th className="py-4 px-4 font-semibold text-center">Privluxe</th>
                  <th className="py-4 px-4 font-semibold text-center" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>Others</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <td className="py-4 px-4">Chat without sharing phone number</td>
                  <td className="py-4 px-4 text-center"><CheckCircle className="h-5 w-5 mx-auto" style={{ color: '#22D3EE' }} /></td>
                  <td className="py-4 px-4 text-center"><X className="h-5 w-5 mx-auto" style={{ color: 'rgba(255,255,255,0.3)' }} /></td>
                </tr>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <td className="py-4 px-4">Anonymous invite links</td>
                  <td className="py-4 px-4 text-center"><CheckCircle className="h-5 w-5 mx-auto" style={{ color: '#22D3EE' }} /></td>
                  <td className="py-4 px-4 text-center"><X className="h-5 w-5 mx-auto" style={{ color: 'rgba(255,255,255,0.3)' }} /></td>
                </tr>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <td className="py-4 px-4">Identity stays hidden</td>
                  <td className="py-4 px-4 text-center"><CheckCircle className="h-5 w-5 mx-auto" style={{ color: '#22D3EE' }} /></td>
                  <td className="py-4 px-4 text-center"><X className="h-5 w-5 mx-auto" style={{ color: 'rgba(255,255,255,0.3)' }} /></td>
                </tr>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <td className="py-4 px-4">View-once messages</td>
                  <td className="py-4 px-4 text-center"><CheckCircle className="h-5 w-5 mx-auto" style={{ color: '#22D3EE' }} /></td>
                  <td className="py-4 px-4 text-center" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>Some</td>
                </tr>
                <tr>
                  <td className="py-4 px-4">Free to use</td>
                  <td className="py-4 px-4 text-center"><CheckCircle className="h-5 w-5 mx-auto" style={{ color: '#22D3EE' }} /></td>
                  <td className="py-4 px-4 text-center"><CheckCircle className="h-5 w-5 mx-auto" style={{ color: 'rgba(255,255,255,0.3)' }} /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* TRUST SECTION - Strengthened with Direct Statements */}
      <section className="py-20 px-4" style={{ background: 'rgba(10, 15, 44, 0.5)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <Shield className="h-12 w-12 mx-auto mb-6" style={{ color: '#7C3AED' }} />
          <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#E5E7EB' }}>
            What Privluxe Does Not Do
          </h2>
          <p className="text-lg mb-10 max-w-2xl mx-auto" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>
            We built Privluxe specifically for private communication. Here's what we guarantee:
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-left max-w-2xl mx-auto">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 mt-1 shrink-0" style={{ color: '#22D3EE' }} />
              <p style={{ color: 'rgba(229, 231, 235, 0.8)' }}>We do not share your phone number with other users</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 mt-1 shrink-0" style={{ color: '#22D3EE' }} />
              <p style={{ color: 'rgba(229, 231, 235, 0.8)' }}>We do not reveal your email to anyone you chat with</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 mt-1 shrink-0" style={{ color: '#22D3EE' }} />
              <p style={{ color: 'rgba(229, 231, 235, 0.8)' }}>We do not tell others if you're using Privluxe</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 mt-1 shrink-0" style={{ color: '#22D3EE' }} />
              <p style={{ color: 'rgba(229, 231, 235, 0.8)' }}>You control what you share and when</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ SECTION - Most Important for AEO */}
      <section id="faq" className="py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4" style={{ color: '#E5E7EB' }}>
            Frequently Asked Questions
          </h2>
          <p className="text-center mb-12" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>
            Everything you need to know about private messaging with Privluxe
          </p>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => {
              const isOpen = openFaq === index;
              // Get first sentence or first ~80 chars as preview
              const firstSentence = faq.a.split('. ')[0] + '.';
              const hasMore = faq.a.length > firstSentence.length;
              
              return (
                <div 
                  key={index}
                  className="rounded-xl overflow-hidden"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  <div className="p-5">
                    <button
                      onClick={() => setOpenFaq(isOpen ? null : index)}
                      className="w-full flex items-center justify-between text-left"
                    >
                      <span className="font-medium pr-4" style={{ color: '#E5E7EB' }}>{faq.q}</span>
                      {hasMore && (
                        <ChevronDown 
                          className={`h-5 w-5 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                          style={{ color: '#7C3AED' }}
                        />
                      )}
                    </button>
                    {/* Always show first line */}
                    <p className="mt-3" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>
                      {isOpen ? faq.a : firstSentence}
                      {!isOpen && hasMore && (
                        <button 
                          onClick={() => setOpenFaq(index)}
                          className="ml-2 text-sm hover:underline"
                          style={{ color: '#7C3AED' }}
                        >
                          Read more
                        </button>
                      )}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* INTERNAL DEPTH PAGES - AEO Internal Linking */}
      <section className="py-16 px-4" style={{ background: 'rgba(10, 15, 44, 0.3)' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8" style={{ color: '#E5E7EB' }}>
            Learn More About Private Messaging
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a 
              href="/anonymous-chat" 
              className="p-5 rounded-xl transition-all hover:scale-[1.02]"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <h3 className="font-semibold mb-2" style={{ color: '#E5E7EB' }}>What is Anonymous Chat?</h3>
              <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>Learn how to have conversations without revealing your identity.</p>
              <span className="text-sm mt-2 inline-block" style={{ color: '#7C3AED' }}>Read more →</span>
            </a>
            <a 
              href="/send-message-without-phone-number" 
              className="p-5 rounded-xl transition-all hover:scale-[1.02]"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <h3 className="font-semibold mb-2" style={{ color: '#E5E7EB' }}>Message Without Phone Number</h3>
              <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>How to chat with someone without sharing your number.</p>
              <span className="text-sm mt-2 inline-block" style={{ color: '#7C3AED' }}>Read more →</span>
            </a>
            <a 
              href="/private-messaging-guide" 
              className="p-5 rounded-xl transition-all hover:scale-[1.02]"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <h3 className="font-semibold mb-2" style={{ color: '#E5E7EB' }}>Private Messaging Guide</h3>
              <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>Complete guide to secure, private communication online.</p>
              <span className="text-sm mt-2 inline-block" style={{ color: '#7C3AED' }}>Read more →</span>
            </a>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 px-4 text-center" style={{ background: 'rgba(124, 58, 237, 0.1)' }}>
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#E5E7EB' }}>
            Ready to Chat Privately?
          </h2>
          <p className="text-lg mb-8" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>
            Start your first anonymous conversation in seconds. No phone number sharing, no identity exposure.
          </p>
          <div className="flex flex-col items-center gap-2">
            <Button 
              onClick={() => handleCTAClick('final_cta')}
              className="h-14 px-10 text-white text-lg font-semibold"
              style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
              data-testid="final-cta-button"
            >
              Start Private Chat
            </Button>
            <span className="text-xs" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>No cost. No identity exposure.</span>
          </div>
        </div>
      </section>

      <Footer />
    </div>
    </>
  );
}
